import type { MaybeRefOrGetter } from 'vue';

import { useApolloClient, useMutation, useQuery } from '@vue/apollo-composable';
import { useDebounceFn, useDocumentVisibility, useEventListener, useIntervalFn } from '@vueuse/core';
import { computed, getCurrentInstance, nextTick, onMounted, onUnmounted, shallowRef, toValue, watch } from 'vue';

import type { GetOrderFiscalOperationalStatusQuery, ShiftFieldsFragment } from '@/entities/fiscalization';
import type { KkmHandshakeResult, KkmServerAdapter as KkmServerAdapterType } from '@/shared/api';

import { useAuthStore } from '@/entities/auth';
import {
  fiscalShiftState,
  isKktConcern,
  useFiscalWorkstationStorage,
  useKktHealthStore,
} from '@/entities/fiscalization';
import { toLocationZone } from '@/entities/location';
import { apolloOperationGate, getKkmServerAdapter } from '@/shared/api';
import { requirePayload, useToast } from '@/shared/lib';
import {
  AbortCashShiftOpeningDocument,
  CancelCashShiftClosingDocument,
  CashShiftClosingContextDocument,
  CloseCashShiftDocument,
  ConfirmCashShiftKktDocument,
  ForceCloseCashShiftDocument,
  GetCurrentShiftDocument,
  GetOrderFiscalOperationalStatusDocument,
  OpenCashShiftDocument,
  OrderFiscalOperationType,
  RegisterCashShiftKktReopenDocument,
  ShiftStatus,
  StartCashShiftClosingDocument,
} from '@shared/api/graphql/gql/graphql';
import { clock } from '@shared/lib/date-time';

import type {
  CashShiftClosingPreparation,
  CompleteCashShiftClosingRequest,
  ShiftPendingOperation,
} from './shift-closing-controller';

import { ShiftClosingController } from './shift-closing-controller';
import { reportKktShiftOrphan, ShiftKktReopenController } from './shift-kkt-reopen-controller';
import { ShiftOpeningController } from './shift-opening-controller';
import { useOrderFiscalRefund } from './use-order-fiscal-refund';
import { useOrderFiscalization } from './use-order-fiscalization';

const NORMAL_POLL_INTERVAL_MS = 5 * 60 * 1_000;
const BLOCKING_POLL_INTERVAL_MS = 15 * 1_000;
const LIFECYCLE_REFRESH_DEBOUNCE_MS = 250;
/**
 * Запас к паузе между фоновыми опросами локации. Собственный тик потребителя приходит ровно через
 * интервал и обязан пройти, а тик соседнего потребителя со смещением внутри периода — нет.
 */
const POLL_MIN_GAP_RATIO = 0.9;
const MUTATION_OPTIONS = {
  context: { suppressGlobalErrorToast: true },
  // `shift_blocker_reason` живёт в контексте оплаты/возврата, а не в запросах смены: без рефетча
  // открытая панель расчёта показывает «Смена не открыта» до перезагрузки страницы.
  refetchQueries: ['GetOrderCheckoutContext', 'GetOrderRefundContext'],
};
const DEFAULT_ACTION_ERROR_MESSAGE = 'Не удалось изменить состояние смены.';
/**
 * Исход опроса для браузера, из которого кассу не привязывали: на компьютере руководителя
 * ККТ не может ответить, и её молчание — не отказ кассы клуба, а отсутствие кассы здесь.
 */
const REMOTE_WORKSTATION_HANDSHAKE: KkmHandshakeResult = {
  instruction: 'На этом компьютере касса не подключена. Откройте Mors на компьютере у кассы.',
  kind: 'bridge_missing',
};

interface RunShiftActionOptions {
  /** `false` — отказ не поднимается тостом: вызывающий показывает причину сам. */
  reportError?: boolean;
}

/**
 * Исход открытия смены. `recovery_required` — смена уже создана в Морсе и висит в `opening`,
 * но касса открытие не подтвердила: возвращать кассира к форме открытия нельзя, из этого
 * состояния есть только «Продолжить» и «Отменить».
 */
export type OpenShiftOutcome = 'cancelled' | 'failed' | 'opened' | 'recovery_required';

type ShiftStatusAdapter = Pick<
  KkmServerAdapterType,
  'closeShift' | 'getCounters' | 'getResult' | 'handshake' | 'openShift' | 'xReport'
>;
/** Фоновый тик опроса уступает дорогу денежной операции; ручное обновление — нет. */
type RefreshOrigin = 'manual' | 'poll';
type FiscalOperationalStatus = GetOrderFiscalOperationalStatusQuery['orderFiscalOperationalStatus'];
type PendingShiftAction =
  | 'cancel_closing'
  | 'cancel_opening'
  | 'close'
  | 'continue_opening'
  | 'force_close'
  | 'open'
  | 'reopen'
  | 'start_closing'
  | 'x_report';

export interface UseFiscalShiftStatusOptions {
  adapter?: ShiftStatusAdapter;
  /**
   * Как обновляться при монтировании, фокусе и видимости. Плашка пересоздаётся на каждой
   * странице и передаёт `'poll'`: иначе каждый переход опрашивал бы кассу без паузы.
   */
  backgroundRefresh?: RefreshOrigin;
  enabled?: MaybeRefOrGetter<boolean>;
  locationId: MaybeRefOrGetter<string | null | undefined>;
}

export function useFiscalShiftStatus(options: UseFiscalShiftStatusOptions) {
  const toast = useToast();
  const adapter = options.adapter ?? getKkmServerAdapter();
  const operationalStatus = shallowRef<FiscalOperationalStatus | null>(null);
  const currentShift = shallowRef<ShiftFieldsFragment | null>(null);
  const closingPreparation = shallowRef<CashShiftClosingPreparation | null>(null);
  const refreshError = shallowRef<unknown>(null);
  /** Запрос смены уже отвечал — иначе `currentShift === null` читается как незнание. */
  const shiftLoaded = shallowRef(false);
  const isRefreshingStatus = shallowRef(false);
  const pendingAction = shallowRef<PendingShiftAction | null>(null);
  /** Причина последнего отказа действия — для тех мест, где её показывают внутри модалки. */
  const actionError = shallowRef<string | null>(null);
  /** Закрыть смену не вышло: рядом с повтором предлагается аварийное закрытие. */
  const closeRefused = shallowRef(false);
  /** Шаг цикла смены: «Допечатываем чеки: N». */
  const cycleProgress = shallowRef<string | null>(null);
  const now = shallowRef(clock.now());
  useIntervalFn(() => {
    now.value = clock.now();
  }, 30_000);
  const { resolveClient } = useApolloClient();
  const locationId = computed(() => toValue(options.locationId) ?? '');
  const kktHealth = useKktHealthStore();
  const authStore = useAuthStore();
  const locationZone = computed(() => toLocationZone(authStore.currentLocation?.timezone));
  const handshake = kktHealth.handshakeFor(locationId);
  const isPollingKkt = kktHealth.isPollingFor(locationId);
  /**
   * Касса привязана к другому браузеру: её идентификатор лежит в localStorage кассового
   * компьютера и в `browser_workstation_id`. Несовпадение отличает чужой компьютер, где
   * Морс открыт лишь посмотреть, от кассового.
   */
  const foreignWorkstation = computed(() => {
    const organizationId = authStore.currentOrganization?.id;
    const boundId = operationalStatus.value?.browser_workstation_id ?? null;

    if (organizationId === undefined || locationId.value === '' || boundId === null) return false;

    return useFiscalWorkstationStorage(organizationId, locationId.value).state.value.browserWorkstationId !== boundId;
  });
  /** Браузер подтверждённо работает кассой этой локации. */
  const isWorkstationBound = computed(() => {
    const boundId = operationalStatus.value?.browser_workstation_id ?? null;

    return boundId !== null && !foreignWorkstation.value;
  });
  /** Ready-ответ доказывает, что касса физически здесь, даже если localStorage чистили после привязки. */
  const effectiveHandshake = computed(() =>
    foreignWorkstation.value && handshake.value?.kind !== 'ready' ? REMOTE_WORKSTATION_HANDSHAKE : handshake.value,
  );
  const enabled = computed(
    () => (options.enabled === undefined ? true : toValue(options.enabled)) && locationId.value !== '',
  );
  const variables = computed(() => ({ locationId: locationId.value }));
  const {
    error: operationalQueryError,
    loading: operationalQueryLoading,
    refetch: refetchOperationalStatus,
    result: operationalQueryResult,
    start: startOperationalStatusQuery,
  } = useQuery(GetOrderFiscalOperationalStatusDocument, variables, () => ({
    enabled: enabled.value,
    fetchPolicy: 'cache-and-network',
  }));
  const {
    error: shiftQueryError,
    loading: shiftQueryLoading,
    refetch: refetchCurrentShift,
    result: currentShiftQueryResult,
    start: startCurrentShiftQuery,
  } = useQuery(GetCurrentShiftDocument, variables, () => ({
    enabled: enabled.value,
    fetchPolicy: 'cache-and-network',
  }));
  const { mutate: openMutation } = useMutation(OpenCashShiftDocument, MUTATION_OPTIONS);
  const { mutate: confirmMutation } = useMutation(ConfirmCashShiftKktDocument, MUTATION_OPTIONS);
  const { mutate: abortMutation } = useMutation(AbortCashShiftOpeningDocument, MUTATION_OPTIONS);
  const { mutate: startClosingMutation } = useMutation(StartCashShiftClosingDocument, MUTATION_OPTIONS);
  const { mutate: closeMutation } = useMutation(CloseCashShiftDocument, MUTATION_OPTIONS);
  const { mutate: cancelClosingMutation } = useMutation(CancelCashShiftClosingDocument, MUTATION_OPTIONS);
  const { mutate: registerReopenMutation } = useMutation(RegisterCashShiftKktReopenDocument, MUTATION_OPTIONS);
  const { mutate: forceCloseMutation } = useMutation(ForceCloseCashShiftDocument, MUTATION_OPTIONS);
  const fiscalScope = {
    expectedKktDeviceId: computed(() => operationalStatus.value?.expected_kkt_device_id),
    locationId,
    organizationId: computed(() => authStore.currentOrganization?.id),
  };
  const settlements = useOrderFiscalization(fiscalScope);
  const refunds = useOrderFiscalRefund(fiscalScope);
  const readClosingContext = async (shiftId: string) => {
    const response = await resolveClient().query({
      fetchPolicy: 'network-only',
      query: CashShiftClosingContextDocument,
      variables: { shiftId },
    });

    return requirePayload(response.data.shiftClosingContext, 'Морс не отдал состояние закрытия смены.');
  };
  /**
   * Цикл смены: операции локации, ждущие чека, доводятся по одной. Новых оплат цикл не
   * начинает и вопрос «Чек вышел?» не задаёт; операцию другой ККТ контроллер не тронет.
   * Недоведённая операция остаётся проблемной и смене не мешает, поэтому цикл не бросает.
   */
  const finishPendingOperations = async (operations: readonly ShiftPendingOperation[]): Promise<void> => {
    let left = operations.length;

    for (const operation of operations) {
      cycleProgress.value = `Допечатываем чеки: ${left}`;
      left -= 1;
      const owner = operation.operation_type === OrderFiscalOperationType.Refund ? refunds : settlements;
      const phase = await owner.recoverInShiftCycle(operation.order_id).catch(() => null);

      if (phase !== 'completed' && phase !== 'failed_before_print') {
        console.warn('[useFiscalShiftStatus.finishPendingOperations]', { operationId: operation.id, phase });
      }
    }

    cycleProgress.value = null;
  };
  /** После открытия смены: допечатка, в том числе чеками коррекции за закрытые смены. */
  const finishPendingAfterOpening = async (shiftId: string): Promise<void> => {
    if (!requireOperationalStatus().fiscalization_enabled) return;

    const context = await readClosingContext(shiftId).catch(() => null);

    if (context !== null) await finishPendingOperations(context.pending_operations);
  };
  const openingController = new ShiftOpeningController(adapter, {
    abort: async (shiftId) =>
      requirePayload((await abortMutation({ shiftId }))?.data?.abortShiftOpening, 'Морс не отменил открытие смены.'),
    confirmKkt: async (input) =>
      requirePayload((await confirmMutation({ input }))?.data?.confirmShiftKkt, 'Морс не подтвердил смену ККТ.'),
    open: async (input) => requirePayload((await openMutation({ input }))?.data?.openShift, 'Морс не открыл смену.'),
  });
  const closingController = new ShiftClosingController(adapter, {
    cancel: async (shiftId) =>
      requirePayload(
        (await cancelClosingMutation({ shiftId }))?.data?.cancelShiftClosing,
        'Морс не отменил закрытие смены.',
      ),
    close: async (input) => requirePayload((await closeMutation({ input }))?.data?.closeShift, 'Морс не закрыл смену.'),
    finishPendingOperations,
    forceClose: async (input) =>
      requirePayload(
        (await forceCloseMutation({ input }))?.data?.forceCloseShift,
        'Морс не выполнил аварийное закрытие.',
      ),
    readContext: readClosingContext,
    start: async (shiftId) =>
      requirePayload(
        (await startClosingMutation({ shiftId }))?.data?.startShiftClosing,
        'Морс не начал закрытие смены.',
      ),
  });
  const kktReopenController = new ShiftKktReopenController(adapter, {
    register: async (input) =>
      requirePayload(
        (await registerReopenMutation({ input }))?.data?.registerShiftKktReopen,
        'Морс не зафиксировал новую смену ККТ.',
      ),
  });
  let refreshPromise: Promise<void> | null = null;
  /**
   * Номер смены ККТ, по которой рассинхрон уже зафиксирован: без него событие писалось бы на каждом тике.
   * `undefined` — ещё не фиксировали; `null` — зафиксировали смену, номер которой касса не вернула.
   */
  let reportedOrphanKktSession: number | null | undefined;

  const state = computed(() => {
    const operational = operationalStatus.value;

    // Ответы по статусу и по смене идут разными запросами: до второго «смены нет» означает
    // «ещё не знаем», и чип ждёт, а не мигает надписью «Смена не открыта».
    if (operational !== null && shiftLoaded.value) {
      return fiscalShiftState(effectiveHandshake.value, operational, currentShift.value, now.value, locationZone.value);
    }

    return refreshError.value === null
      ? null
      : {
          action: null,
          blocking: true,
          description: 'Проверьте подключение и обновите состояние перед оплатой.',
          kktStatePending: false,
          status: 'unavailable' as const,
          title: 'Не удалось проверить кассу',
          tone: 'danger' as const,
        };
  });

  /**
   * Состояние для шапки: чип рассказывает о смене и о том, кто её ведёт. Диагноз кассы
   * туда не поднимается — его место в попапе чипа и в виджетах на стадии оплаты.
   */
  const shiftState = computed(() => {
    const full = state.value;
    const operational = operationalStatus.value;

    return full === null || operational === null || !isKktConcern(full.status)
      ? full
      : fiscalShiftState(null, operational, currentShift.value, now.value, locationZone.value);
  });

  /** Вторая половина того же разделения: диагноз кассы, который в шапку не поднимается. */
  const kktConcern = computed(() => (state.value !== null && isKktConcern(state.value.status) ? state.value : null));

  const pollIntervalMs = computed(() => (state.value?.blocking ? BLOCKING_POLL_INTERVAL_MS : NORMAL_POLL_INTERVAL_MS));

  const refresh = (origin: RefreshOrigin = 'manual'): Promise<void> => {
    if (refreshPromise !== null) return refreshPromise;

    refreshPromise = refreshNow(origin).finally(() => {
      refreshPromise = null;
    });

    return refreshPromise;
  };

  const refreshAfterQueryActivation = async (): Promise<void> => {
    const activationLocationId = locationId.value;

    await nextTick();

    if (!enabled.value || locationId.value !== activationLocationId) {
      return;
    }

    startOperationalStatusQuery();
    startCurrentShiftQuery();
    await refresh();
  };

  const refreshNow = async (origin: RefreshOrigin): Promise<void> => {
    if (!enabled.value) {
      resetState();
      return;
    }

    const nextOperationalStatus = await refreshServerState();

    if (nextOperationalStatus === null) return;

    await refreshKktState(nextOperationalStatus, origin);
  };

  /** Быстрый источник: GraphQL отвечает за сотни миллисекунд и не должен ждать опроса ККТ. */
  const refreshServerState = async (): Promise<FiscalOperationalStatus | null> => {
    isRefreshingStatus.value = true;

    try {
      const [operationalResponse, shiftResponse] = await Promise.all([
        refetchOperationalStatus(),
        refetchCurrentShift(),
      ]);
      const nextOperationalStatus =
        operationalResponse?.data.orderFiscalOperationalStatus ??
        operationalQueryResult.value?.orderFiscalOperationalStatus ??
        null;

      if (nextOperationalStatus === null) {
        throw new Error('Не удалось загрузить состояние смены.');
      }

      operationalStatus.value = nextOperationalStatus;
      currentShift.value = shiftResponse?.data.currentShift ?? currentShiftQueryResult.value?.currentShift ?? null;
      shiftLoaded.value = true;
      refreshError.value = null;

      return nextOperationalStatus;
    } catch (error) {
      // Состояние ККТ не трогаем: отказ GraphQL ничего не говорит о кассе на этом компьютере.
      refreshError.value = error;

      return null;
    } finally {
      isRefreshingStatus.value = false;
    }
  };

  /**
   * Медленный источник: handshake — три команды класса `health` подряд, каждая под своим
   * watchdog'ом (см. `KKM_SERVER_2_3_16_36_PROFILE.timeouts.health`), поэтому неотвечающий
   * KKMServer задерживает такт надолго. Отдельный такт, чтобы это ожидание не задерживало
   * уже полученное серверное состояние смены и не затирало его при отказе.
   *
   * Опрос идёт через общий шлюз, иначе health-команда встаёт в очередь KKMServer перед печатью чека.
   * Фоновый тик при занятом шлюзе пропускается, а не ставится в очередь: денежная операция важнее
   * свежести health, а прежнее состояние ККТ остаётся на экране.
   */
  const refreshKktState = async (operational: FiscalOperationalStatus, origin: RefreshOrigin): Promise<void> => {
    if (!operational.fiscalization_enabled) {
      kktHealth.forget(locationId.value);
      return;
    }

    if (origin === 'poll' && apolloOperationGate.isRunning()) return;
    // Чужой браузер опрашивает кассу один раз: если она не ответила, значит её здесь нет.
    if (foreignWorkstation.value && origin === 'poll' && handshake.value !== null && handshake.value.kind !== 'ready') {
      return;
    }

    let polledFresh = false;

    try {
      polledFresh = await kktHealth.poll({
        execute: () =>
          apolloOperationGate.runExclusive('kkt-health', () =>
            // Единственный фоновый опрос: только его журнал вправе дедуплицировать.
            adapter.handshake({ diagnostic: true, kktDeviceId: operational.expected_kkt_device_id ?? undefined }),
          ),
        locationId: locationId.value,
        minIntervalMs: origin === 'poll' ? pollIntervalMs.value * POLL_MIN_GAP_RATIO : undefined,
      });
    } catch (error) {
      // `adapter.handshake` сам не бросает — сюда приводит только неожиданный отказ шлюза.
      refreshError.value = error;
      return;
    }

    const polled = handshake.value;

    if (polledFresh && polled?.kind === 'ready') {
      kktReopenController.detect(currentShift.value, polled);
      reportOrphanKktShiftOnce(polled.kkt.sessionNumber);
    }
  };

  const openShift = async (
    openingCash: number,
    confirmExistingKktShift?: (sessionState: 2 | 3) => Promise<boolean>,
  ): Promise<OpenShiftOutcome> => {
    const outcome = await runAction(
      'open',
      async () => {
        const operational = requireOperationalStatus();
        const result = await openingController.open({
          cashierName: operational.cashier_display_name ?? '',
          confirmExistingKktShift,
          expectedKktDeviceId: operational.expected_kkt_device_id,
          fiscalizationEnabled: operational.fiscalization_enabled,
          locationId: locationId.value,
          openingCash,
        });

        if (result.kind === 'cancelled') return 'cancelled' as const;

        toast.show('Смена открыта');
        await finishPendingAfterOpening(result.shift.id);

        return 'opened' as const;
      },
      { reportError: false },
    );

    if (outcome !== false) return outcome;

    // Смена уже создана и висит в `opening`: форма открытия здесь бесполезна — повторный
    // сабмит упрётся в `shifts_one_active_per_location`. Причину показывает сама модалка
    // восстановления, поэтому тост не поднимаем.
    if (currentShift.value?.status === ShiftStatus.Opening) return 'recovery_required';

    toast.error(actionError.value ?? DEFAULT_ACTION_ERROR_MESSAGE);

    return 'failed';
  };

  const continueOpening = async (): Promise<boolean> =>
    runAction('continue_opening', async () => {
      const shift = requireCurrentShift();
      const operational = requireOperationalStatus();
      const opened = await openingController.continueOpening({
        cashierName: operational.cashier_display_name ?? '',
        cashierVatin: shift.cashier_vatin,
        expectedKktDeviceId: operational.expected_kkt_device_id,
        shift,
      });
      toast.show('Открытие смены завершено');
      await finishPendingAfterOpening(opened.id);
      return true;
    });

  const cancelOpening = async (): Promise<boolean> =>
    runAction('cancel_opening', async () => {
      await openingController.cancelOpening(requireCurrentShift().id);
      toast.show('Открытие смены отменено');
      return true;
    });

  const prepareClosing = async (): Promise<CashShiftClosingPreparation | null> => {
    const prepared = await runAction('start_closing', async () => {
      const shift = requireCurrentShift();
      const operational = requireOperationalStatus();
      return closingController.prepare({
        expectedKktDeviceId: operational.expected_kkt_device_id,
        fiscalizationEnabled: operational.fiscalization_enabled,
        shiftId: shift.id,
      });
    });
    closingPreparation.value = prepared || null;
    closeRefused.value = false;
    return prepared || null;
  };

  const completeClosing = async (
    input: Pick<CompleteCashShiftClosingRequest, 'closingCash' | 'comment'>,
  ): Promise<boolean> => {
    const closed = await runAction('close', async () => {
      const shift = requireCurrentShift();
      const operational = requireOperationalStatus();
      const revenueKkt = closingPreparation.value?.kind === 'ready' ? closingPreparation.value.counters.total : null;
      await closingController.complete({
        ...input,
        cashierName: operational.cashier_display_name ?? '',
        cashierVatin: shift.cashier_vatin,
        expectedKktDeviceId: operational.expected_kkt_device_id,
        fiscalizationEnabled: operational.fiscalization_enabled,
        revenueKkt,
        shiftId: shift.id,
      });
      closingPreparation.value = null;
      toast.show('Смена закрыта');
      return true;
    });
    closeRefused.value = !closed;

    return closed;
  };

  const cancelClosing = async (): Promise<boolean> =>
    runAction('cancel_closing', async () => {
      const operational = requireOperationalStatus();
      await closingController.cancel({
        expectedKktDeviceId: operational.expected_kkt_device_id,
        fiscalizationEnabled: operational.fiscalization_enabled,
        shiftId: requireCurrentShift().id,
      });
      closingPreparation.value = null;
      toast.show('Закрытие смены отменено');
      return true;
    });

  const printXReport = async (): Promise<boolean> =>
    runAction('x_report', async () => {
      const deviceId = closingPreparation.value?.kind === 'ready' ? closingPreparation.value.deviceId : null;

      if (deviceId === null) {
        throw new Error('Касса для X-отчёта не выбрана.');
      }

      await closingController.printXReport(deviceId);
      toast.show('X-отчёт напечатан');
      return true;
    });

  const reopenShift = async (): Promise<boolean> =>
    runAction('reopen', async () => {
      const shift = requireCurrentShift();
      const operational = requireOperationalStatus();
      await kktReopenController.reopen({
        cashierName: operational.cashier_display_name ?? '',
        cashierVatin: shift.cashier_vatin,
        expectedKktDeviceId: operational.expected_kkt_device_id,
        shift,
      });
      toast.show('На кассе открыта новая смена');
      return true;
    });

  const forceClose = async (closingCash: number, comment: string): Promise<boolean> =>
    runAction('force_close', async () => {
      await closingController.forceClose({
        closingCash,
        comment,
        shiftId: requireCurrentShift().id,
      });
      closingPreparation.value = null;
      toast.show('Смена аварийно закрыта');
      return true;
    });

  const runAction = async <Result>(
    action: PendingShiftAction,
    execute: () => Promise<Result>,
    options: RunShiftActionOptions = {},
  ): Promise<Result | false> => {
    pendingAction.value = action;
    actionError.value = null;

    try {
      // Действия смены идут через тот же шлюз, что оплаты и возвраты: иначе они спорят с
      // health-опросом за `deviceKey`, и мьютекс адаптера отказывает мгновенно (`device_busy`).
      // Внутрь обёртки уходит только `execute`: `refresh()` сам берёт шлюз под `kkt-health`,
      // а вложенный `runExclusive` ждал бы собственный `queueTail` — это дедлок.
      const result = await apolloOperationGate.runExclusive('fiscalization', execute);
      await refresh();
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : DEFAULT_ACTION_ERROR_MESSAGE;
      actionError.value = message;

      // Серверное состояние перечитывается и на отказе: запись смены могла успеть появиться,
      // и от неё зависит, что показать кассиру — форму открытия или восстановление.
      // Берём только быстрый источник: опрос ККТ на этот выбор не влияет.
      await refreshServerState();

      if (options.reportError !== false) toast.error(message);

      return false;
    } finally {
      pendingAction.value = null;
    }
  };

  const documentVisibility = useDocumentVisibility();
  const backgroundRefresh = options.backgroundRefresh ?? 'manual';
  const lifecycleRefresh = useDebounceFn(() => refresh(backgroundRefresh), LIFECYCLE_REFRESH_DEBOUNCE_MS);
  const interval = useIntervalFn(
    () => {
      if (enabled.value && documentVisibility.value === 'visible') {
        void refresh('poll');
      }
    },
    pollIntervalMs,
    { immediate: false },
  );

  watch(documentVisibility, (visibility) => {
    if (visibility === 'visible' && enabled.value) void lifecycleRefresh();
  });
  useEventListener(typeof window === 'undefined' ? undefined : window, 'focus', () => {
    if (enabled.value) void lifecycleRefresh();
  });
  watch(currentShiftQueryResult, (queryResult) => {
    if (!enabled.value || queryResult === undefined) return;

    const nextShift = queryResult.currentShift ?? null;

    if (nextShift === null || nextShift.location_id === locationId.value) {
      currentShift.value = nextShift;
      shiftLoaded.value = true;
    }
  });
  // Без этого watch объявленный `cache-and-network` не доставлял бы кэш-хит: `refreshNow` ждёт сеть.
  // Локацию сверить нечем — в `OrderFiscalOperationalStatus` её нет, запрос отличает только `variables`.
  watch(operationalQueryResult, (queryResult) => {
    const nextOperationalStatus = queryResult?.orderFiscalOperationalStatus;

    if (!enabled.value || nextOperationalStatus == null) return;

    operationalStatus.value = nextOperationalStatus;
  });
  watch(enabled, (isEnabled) => {
    if (isEnabled) {
      interval.resume();
      void refreshAfterQueryActivation();
    } else {
      interval.pause();
      resetState();
    }
  });
  if (getCurrentInstance() !== null) {
    onMounted(() => {
      if (enabled.value) {
        interval.resume();
        void refresh(backgroundRefresh);
      }
    });
    onUnmounted(() => interval.pause());
  }

  function reportOrphanKktShiftOnce(sessionNumber: number | null): void {
    if (state.value?.status !== 'kkt_shift_orphan' || reportedOrphanKktSession === sessionNumber) {
      return;
    }

    reportedOrphanKktSession = sessionNumber;
    reportKktShiftOrphan({ locationId: locationId.value, sessionNumber });
  }

  function requireOperationalStatus(): FiscalOperationalStatus {
    if (operationalStatus.value === null) {
      throw new Error('Состояние смены ещё не загружено.');
    }

    return operationalStatus.value;
  }

  function requireCurrentShift(): ShiftFieldsFragment {
    if (currentShift.value === null) {
      throw new Error('Открытая смена не найдена. Обновите страницу.');
    }

    return currentShift.value;
  }

  /** Состояние ККТ общее для локации и живёт в store — свои поля чистит только этот потребитель. */
  function resetState(): void {
    operationalStatus.value = null;
    currentShift.value = null;
    shiftLoaded.value = false;
    closingPreparation.value = null;
    closeRefused.value = false;
    refreshError.value = null;
    reportedOrphanKktSession = undefined;
  }

  return {
    actionError,
    cancelClosing,
    cancelOpening,
    closeRefused,
    closingPreparation,
    completeClosing,
    continueOpening,
    currentShift,
    cycleProgress,
    error: computed(() => refreshError.value ?? operationalQueryError.value ?? shiftQueryError.value),
    forceClose,
    handshake,
    isActionPending: computed(() => pendingAction.value !== null),
    /** Только серверные данные: отрисовка чипа не должна зависеть от медленного опроса ККТ. */
    isLoading: computed(() => operationalQueryLoading.value || shiftQueryLoading.value || isRefreshingStatus.value),
    isPollingKkt,
    isWorkstationBound,
    kktConcern,
    openShift,
    operationalStatus,
    pendingAction,
    pollIntervalMs,
    prepareClosing,
    printXReport,
    refresh,
    reopenShift,
    shiftState,
    state,
  };
}

export type UseFiscalShiftStatusReturn = ReturnType<typeof useFiscalShiftStatus>;
